import React, { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom hook for long press with scroll detection
const useLongPress = (
  onClick,
  onLongPress,
  { ms = 400, moveThreshold = 10 } = {}
) => {
  const timerRef = useRef(null);
  const isLongPress = useRef(false);
  const isScrolling = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  const start = useCallback((e) => {
    isLongPress.current = false;
    isScrolling.current = false;
    const evt = e.touches ? e.touches[0] : e;
    startPos.current = { x: evt.clientX, y: evt.clientY };
    
    timerRef.current = setTimeout(() => {
      isLongPress.current = true;
      onLongPress();
    }, ms);
  }, [onLongPress, ms]);

  const stop = useCallback((e) => {
    clearTimeout(timerRef.current);
    if (isLongPress.current || isScrolling.current) {
      if (e) e.preventDefault();
      return;
    }
    onClick();
  }, [onClick]);

  const move = useCallback((e) => {
    if (isScrolling.current) return;
    const evt = e.touches ? e.touches[0] : e;
    const dx = Math.abs(evt.clientX - startPos.current.x);
    const dy = Math.abs(evt.clientY - startPos.current.y);

    if (dx > moveThreshold || dy > moveThreshold) {
      isScrolling.current = true;
      clearTimeout(timerRef.current);
    }
  }, [moveThreshold]);

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
  }, []);

  return {
    onMouseDown: start,
    onMouseUp: stop,
    onMouseMove: move,
    onTouchStart: start,
    onTouchEnd: stop,
    onTouchMove: move,
    onMouseLeave: cancel,
  };
};

const CoinCard = ({ coin, isSelected, onSelect }) => {
  const navigate = useNavigate();

  // Action for a short click
  const handleClick = useCallback(() => {
    if (isSelected) {
      onSelect(coin.id); // If selected, a short click deselects
    } else {
      navigate(`/coins/${coin.id}`); // If not selected, navigate
    }
  }, [isSelected, navigate, onSelect, coin.id]);

  // Action for a long press
  const handleLongPress = useCallback(() => {
    onSelect(coin.id); // Long press always toggles selection
  }, [onSelect, coin.id]);

  const longPressEvents = useLongPress(handleClick, handleLongPress);

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-3 flex space-x-3 ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}
      {...longPressEvents}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex-grow flex items-start space-x-3 pointer-events-none">
        {coin.imageUrl && (
          <img src={coin.imageUrl} alt={coin.description} className="h-20 w-20 object-contain rounded-md flex-shrink-0" />
        )}
        <div className="flex-grow">
          <p className="text-base font-bold text-gray-900">{coin.description}</p>
          <p className="text-sm text-gray-600">{coin.year} {coin.denomination}</p>
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-600 font-medium">{coin.grade}</p>
            {coin.cac_sticker && coin.cac_sticker !== 'none' && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                coin.cac_sticker === 'gold'
                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                  : 'bg-green-100 text-green-800 border border-green-200'
              }`}>
                CAC
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{coin.country}</p>
          <div className="flex items-center space-x-2 mt-2">
            {coin.status_name && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800`}>
                {coin.status_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinCard;
